import React, { memo } from 'react'
import { Stack, TableRow } from '@mui/material'
import {
  faCopy,
  faFileCsv,
  faLink,
  faPause,
  faPlay
} from '@fortawesome/pro-light-svg-icons'

import DashboardCard from '../shared/DashboardCard/DashboardCard'
import DataTable from '../shared/DataTable/DataTable'
import TableCellData from '../shared/DataTable/TableCellData'
import CustomButton from '../shared/buttons/CustomButton'
import FaIcon from '../shared/FaIcon/FaIcon'
import StatusLabel from '../shared/StatusLabel/StatusLabel'
import {
  buildStatusLabelColor,
  CAMPAIGN_STATUS_OPTIONS
} from '../../util/campaign.helpers'
import { formatToLongDate } from '../../util/date.helpers'
import { copyToClipboard } from '../../util/app/app.helpers'
import { formatTicks } from '../../util/string.helpers'
import { isNumberType } from '../../util/type.helpers'

const CampaignsData = ({
  t,
  tableHeaders,
  onCSVDownload,
  onActivate,
  tokenProfile: { symbol },
  campaignListProfile: { data, loading, pagination },
  selected,
  isListUpdate,
  onChangeCampaignsPage,
  onCampaignsPerPageChange,
  loyaltyProgramUrl
}) => {
  const tableRows = data.map((item, index) => (
    <DataTableRow
      key={index}
      {...item}
      symbol={symbol}
      disabled={isListUpdate && item.id === selected?.id}
      onCSVDownload={() => onCSVDownload(item)}
      onActivate={() => onActivate(item, true)}
      onDeactivate={() => onActivate(item, false)}
      loyaltyProgramUrl={loyaltyProgramUrl}
      t={t}
    />
  ))
  return (
    <DashboardCard>
      <DataTable
        headers={tableHeaders}
        data={tableRows}
        onChangePage={onChangeCampaignsPage}
        onRowsPerPageChange={onCampaignsPerPageChange}
        pagination={pagination}
        sx={{
          whiteSpace: {
            xs: 'nowrap',
            sm: 'unset'
          }
        }}
        noRecordsMessage={
          loading
            ? t('campaign.loading-campaigns')
            : !tableRows.length && t('campaign.no-campaigns-found')
        }
        t={t}
      />
    </DashboardCard>
  )
}

export default memo(CampaignsData)

const DataTableRow = ({
  name,
  value,
  supply,
  codes,
  validUntil,
  status,
  onCSVDownload,
  onActivate,
  onDeactivate,
  disabled,
  loyaltyProgramUrl,
  t
}) => {
  const isActivated = status === CAMPAIGN_STATUS_OPTIONS.ACTIVE
  const codesLength = codes.length
  return (
    <TableRow>
      <TableCellData>{name}</TableCellData>
      <TableCellData>
        {!codesLength ? (
          t('campaign.no-codes')
        ) : codesLength === 1 ? (
          <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }}>
            <CustomButton
              fullWidth={false}
              label={t(codes[0])}
              onClick={() => copyToClipboard(codes[0])}
              endIcon={<FaIcon icon={faCopy} />}
              noPadding
            />
            <CustomButton
              fullWidth={false}
              label={<FaIcon icon={faLink} size='lg' />}
              onClick={() =>
                copyToClipboard(`${loyaltyProgramUrl}?code=${codes[0]}`)
              }
            />
          </Stack>
        ) : (
          <CustomButton
            fullWidth={false}
            label={t('campaign.download-to-get-codes')}
            onClick={onCSVDownload}
            startIcon={<FaIcon icon={faFileCsv} />}
            noPadding
          />
        )}
      </TableCellData>
      <TableCellData>
        {isNumberType(value) ? formatTicks(value) : value}
      </TableCellData>
      <TableCellData>{formatTicks(supply)}</TableCellData>
      <TableCellData noTag>
        <StatusLabel
          label={t(`campaign.campaign-status-types.${status}`)}
          color={buildStatusLabelColor(status)}
          size={10}
        />
      </TableCellData>
      <TableCellData>{formatToLongDate(validUntil)}</TableCellData>
      <TableCellData>
        <Stack
          spacing={1}
          direction={{ xs: 'column', sm: 'row' }}
          sx={{ alignItems: 'center', justifyContent: 'center' }}
        >
          {isActivated ? (
            <CustomButton
              fullWidth={false}
              label={<FaIcon icon={faPause} size='lg' />}
              onClick={onDeactivate}
              disabled={disabled}
              color='error'
            />
          ) : (
            <CustomButton
              fullWidth={false}
              label={<FaIcon icon={faPlay} size='lg' />}
              onClick={onActivate}
              disabled={disabled}
              color='success'
            />
          )}
        </Stack>
      </TableCellData>
    </TableRow>
  )
}
